/**
 * ASCII DXF stream writer (docs/opencadstudio-parity/01-native-dxf-export.md
 * §3.1). Buffers group-code/value pairs as lines and joins once at the end
 * (10k+ entity drawings must not pay quadratic string concatenation).
 */

/** Format a float the way DXF text pairs expect: PLAIN decimal notation
 *  (no exponent — strict readers reject "1e-7"), ~10 significant digits,
 *  trailing zeros trimmed, -0 normalized to 0. */
export function dxfNum(v: number): string {
    if (!Number.isFinite(v)) return '0';
    if (Object.is(v, -0)) v = 0;
    if (Number.isInteger(v) && Math.abs(v) < 1e21) return String(v);
    const magnitude = Math.floor(Math.log10(Math.abs(v)));
    const decimals = Math.min(17, Math.max(0, 10 - magnitude));
    let s = v.toFixed(decimals);
    if (s.indexOf('e') !== -1 || s.indexOf('E') !== -1) return String(v); // ≥1e21 edge
    if (s.indexOf('.') !== -1) s = s.replace(/0+$/, '').replace(/\.$/, '');
    if (s === '-0' || s === '') s = '0';
    return s;
}

export class DxfStreamWriter {
    private lines: string[] = [];

    /** Emit one group code / value pair (two lines). */
    pair(code: number, value: string | number): this {
        this.lines.push(String(code), typeof value === 'number' ? dxfNum(value) : value);
        return this;
    }

    /** Total group pairs emitted so far (diagnostics). */
    get pairCount(): number {
        return this.lines.length / 2;
    }

    /** Full ASCII DXF text (stream flushed once, at the very end). */
    toString(): string {
        return this.lines.join('\n') + '\n';
    }
}
